import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import Papa from 'papaparse';

/**
 * LineChartComponent - A React component for rendering an interactive line chart
 * 
 * @param {Object} props - Component props
 * @param {string} [props.xLabel="Time"] - Label for the x-axis
 * @param {string} [props.yLabel="Value"] - Label for the y-axis
 * @param {Function} props.onDataUpdate - Callback function when data is updated
 * @param {Array} [props.initialData] - Initial data points for the chart
 * @param {string} [props.curveType="step"] - Type of curve to use ("step", "linear", "monotone", "cardinal", etc.)
 */

const LineChartComponent = ({ xLabel = "Time", yLabel = "Value", onDataUpdate, initialData, curveType = "step" }) => {
  // Ensure initialData is valid, or provide a default
  const validInitialData = useMemo(() => {
    if (!initialData || !Array.isArray(initialData) || initialData.length === 0) {
      return Array.from({length: 24}, (_, i) => ({ x: i, y: 0 }));
    }
    // Make sure all data points have valid x and y values
    return initialData.map(point => ({
      x: typeof point.x === 'number' ? point.x : 0,
      y: typeof point.y === 'number' ? point.y : 0
    }));
  }, [initialData]);

  // State declarations
  const [points, setPoints] = useState(validInitialData);
  const [dragIndex, setDragIndex] = useState(null);
  const [minY, setMinY] = useState(Math.min(...points.map(point => point.y)));
  const [maxY, setMaxY] = useState(Math.max(...points.map(point => point.y)) || 1); // Ensure maxY is not 0
  const [dimensions, setDimensions] = useState({ width: 500, height: 300 }); // Default dimensions
  const containerRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  // Wrap padding object in useMemo
  const padding = useMemo(() => ({ left: 60, right: 20, top: 20, bottom: 40 }), []);

  // Calculate chart dimensions
  const chartWidth = dimensions.width - padding.left - padding.right;
  const chartHeight = dimensions.height * 0.7 - padding.top - padding.bottom; // Reduce height to 70% of container

  /**
   * Scale function for x-axis
   * @param {number} x - Input value
   * @returns {number} Scaled output value
   */
  const xScale = (x) => (x / 24) * chartWidth + padding.left;

  /**
   * Scale function for y-axis
   * @param {number} y - Input value
   * @returns {number} Scaled output value
   */
  const yScale = (y) => chartHeight - ((y - minY) / (maxY - minY)) * chartHeight + padding.top;

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
        if (entries[0]) {
            const { width, height } = entries[0].contentRect;
            setDimensions({ width, height });
        }
    });
    if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);

  /**
   * Effect hook to adjust points when min or max Y values change
   */
  useEffect(() => {
    setPoints(prevPoints => 
      prevPoints.map(point => ({
        ...point,
        y: Math.max(minY, Math.min(maxY, point.y))
      }))
    );
  }, [minY, maxY]);

  /**
   * Effect hook to call onDataUpdate when points change
   */
  useEffect(() => {
    onDataUpdate(points);
  }, [points, onDataUpdate]);

  /**
   * Handler for mouse down event on data points
   */
  const handleMouseDown = useCallback((index) => (e) => {
    setDragIndex(index);
  }, []);

  /**
   * Handler for mouse move event for dragging points
   */
  const handleMouseMove = useCallback((e) => {
    if (dragIndex !== null) {
      const svgRect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY - svgRect.top;
      const chartY = Math.max(padding.top, Math.min(mouseY, dimensions.height - padding.bottom));
      const dataY = Number(((maxY - minY) * (1 - (chartY - padding.top) / chartHeight) + minY).toFixed(2));
      
      setPoints(prevPoints => {
        const newPoints = prevPoints.map((point, index) => 
          index === dragIndex ? { ...point, y: dataY } : point
        );
        onDataUpdate(newPoints);
        return newPoints;
      });
    }
  }, [dragIndex, chartHeight, padding, dimensions.height, minY, maxY, onDataUpdate]);

  /**
   * Handler for mouse up event to end dragging
   */
  const handleMouseUp = useCallback(() => {
    setDragIndex(null);
  }, []);

  /**
   * Generate path data string based on curve type
   */
  const generatePath = () => {
    if (points.length < 2) return '';
    
    let path = `M ${xScale(points[0].x)},${yScale(points[0].y)}`;
    
    if (curveType === "step") {
      // Step line (current default implementation)
      for (let i = 1; i < points.length; i++) {
        const curr = points[i];
        path += ` H ${xScale(curr.x)} V ${yScale(curr.y)}`;
      }
    } else if (curveType === "linear") {
      // Simple linear path
      for (let i = 1; i < points.length; i++) {
        path += ` L ${xScale(points[i].x)},${yScale(points[i].y)}`;
      }
    } else if (curveType === "monotone" || curveType === "smooth") {
      // Smooth curve using cubic bezier
      for (let i = 0; i < points.length - 1; i++) {
        const current = points[i];
        const next = points[i + 1];
        
        // Calculate control points for cubic bezier curve
        const controlX1 = xScale(current.x + (next.x - current.x) / 3);
        const controlY1 = yScale(current.y);
        const controlX2 = xScale(current.x + 2 * (next.x - current.x) / 3);
        const controlY2 = yScale(next.y);
        
        // Add cubic bezier curve segment
        path += ` C ${controlX1},${controlY1} ${controlX2},${controlY2} ${xScale(next.x)},${yScale(next.y)}`;
      }
    } else if (curveType === "cardinal") {
      // Cardinal spline with tension 0.5
      for (let i = 0; i < points.length - 1; i++) {
        const current = points[i];
        const next = points[i + 1];
        
        // Calculate tangent vectors
        const prev = i > 0 ? points[i-1] : current;
        const nextNext = i < points.length - 2 ? points[i+2] : next;
        
        // Tension factor (0.5 is a good default)
        const tension = 0.5;
        
        // Calculate control points
        const dx = (next.x - prev.x) * tension;
        const dy = (next.y - prev.y) * tension;
        
        const dx2 = (nextNext.x - current.x) * tension;
        const dy2 = (nextNext.y - current.y) * tension;
        
        const controlX1 = xScale(current.x + dx/3);
        const controlY1 = yScale(current.y + dy/3);
        const controlX2 = xScale(next.x - dx2/3);
        const controlY2 = yScale(next.y - dy2/3);
        
        path += ` C ${controlX1},${controlY1} ${controlX2},${controlY2} ${xScale(next.x)},${yScale(next.y)}`;
      }
    }
    
    return path;
  };

  const pathD = generatePath();
  const areaPathD = `${pathD} L ${xScale(points[points.length - 1].x)},${yScale(minY)} L ${xScale(points[0].x)},${yScale(minY)} Z`;

  const formatTime = (hour) => {
    const period = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour % 12 || 12;
    return `${displayHour} ${period}`;
  };

  const downloadCSV = () => {
    const csvContent = [
      [xLabel, yLabel],
      ...points.map(point => [formatTime(point.x), point.y.toFixed(2)])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'chart_data.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleUpload = () => {
    if (selectedFile) {
      Papa.parse(selectedFile, {
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            const headers = results.data[0];
            if (headers.length === 2 && 
                headers[0].toLowerCase().includes(xLabel.toLowerCase()) && 
                headers[1].toLowerCase().includes(yLabel.toLowerCase())) {
              const newPoints = results.data.slice(1).map((row, index) => {
                const timeString = row[0];
                const y = parseFloat(row[1]);
                
                // Convert time string to 24-hour format
                const x = convertTo24HourFormat(timeString);
                
                if (isNaN(x) || isNaN(y)) {
                  setError(`Invalid data in row ${index + 2}`);
                  return null;
                }
                return { x, y };
              }).filter(point => point !== null);

              if (newPoints.length > 0) {
                setPoints(newPoints);
                setMinY(Math.min(...newPoints.map(p => p.y)));
                setMaxY(Math.max(...newPoints.map(p => p.y)));
                setError("");
              } else {
                setError("No valid data found in the CSV file");
              }
            } else {
              setError("CSV format is incorrect. Expected headers: Time, Value");
            }
          } else {
            setError("No data found in the CSV file");
          }
        },
        header: false,
        skipEmptyLines: true,
        transformHeader: header => header.trim().toLowerCase(),
      });
    } else {
      setError("No file selected");
    }
  };

  // Helper function to convert time string to 24-hour format
  const convertTo24HourFormat = (timeString) => {
    const [time, period] = timeString.split(' ');
    let [hours] = time.split(':').map(Number); // Remove 'minutes' from here
    
    if (period.toLowerCase() === 'pm' && hours !== 12) {
      hours += 12;
    } else if (period.toLowerCase() === 'am' && hours === 12) {
      hours = 0;
    }
    
    return hours;
  };

  /**
   * This is the main return function for the LineChart component.
   * It renders the SVG chart, input boxes, and axis labels.
   */
  return (
    <div className="w-full">
      <div ref={containerRef} className="w-full h-80 relative">
        <svg width="100%" height="100%" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} className="select-none">
          {/* Grid lines and axes */}
          <g className="grid-lines">
            {Array.from({ length: 5 }).map((_, i) => (
              <line key={i} x1={padding.left} y1={padding.top + i * (chartHeight / 4)} x2={padding.left + chartWidth} y2={padding.top + i * (chartHeight / 4)} stroke="#e5e7eb" strokeWidth="1" />
            ))}
            {Array.from({ length: 7 }).map((_, i) => (
              <line key={i} x1={xScale(i * 4)} y1={padding.top} x2={xScale(i * 4)} y2={padding.top + chartHeight} stroke="#e5e7eb" strokeWidth="1" />
            ))}
          </g>
          <path d={areaPathD} fill="#3b82f6" fillOpacity="0.2" />
          <path d={pathD} fill="none" stroke="#3b82f6" strokeWidth="2" />
          {points.map((point, index) => (
            <circle key={index} cx={xScale(point.x)} cy={yScale(point.y)} r="5" fill="#3b82f6" onMouseDown={handleMouseDown(index)} className="cursor-pointer" />
          ))}
          {/* Axis Labels */}
          <text x={padding.left + chartWidth / 2} y={dimensions.height - 5} textAnchor="middle" className="text-sm fill-current text-gray-600">{xLabel}</text>
          <text transform={`rotate(-90) translate(${-dimensions.height / 2}, 20)`} textAnchor="middle" className="text-sm fill-current text-gray-600">{yLabel}</text>
          {/* Y-axis ticks */}
          {Array.from({ length: 5 }).map((_, i) => {
            const val = minY + (i * (maxY - minY) / 4);
            return <text key={i} x={padding.left - 10} y={yScale(val) + 5} textAnchor="end" className="text-xs fill-current text-gray-500">{val.toFixed(2)}</text>
          })}
          {/* X-axis ticks */}
          {Array.from({ length: 7 }).map((_, i) => {
            const hour = i * 4;
            const period = hour < 12 ? 'AM' : 'PM';
            const displayHour = hour % 12 || 12;
            return <text key={i} x={xScale(hour)} y={padding.top + chartHeight + 20} textAnchor="middle" className="text-xs fill-current text-gray-500">{`${displayHour}${period}`}</text>
          })}
        </svg>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm">
        <div className="flex items-center gap-4">
          <input type="file" accept=".csv" onChange={handleFileSelect} className="text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"/>
          <button onClick={handleUpload} disabled={!selectedFile} className="px-4 py-2 bg-blue-500 text-white rounded-md disabled:bg-gray-300">Upload CSV</button>
          <button onClick={downloadCSV} className="px-4 py-2 bg-gray-600 text-white rounded-md">Download Template</button>
        </div>
        <div className="flex items-center gap-4">
            <span className="font-medium text-gray-700">Y-Axis Range:</span>
            <input type="number" value={minY} onChange={e => setMinY(Number(e.target.value))} className="w-20 px-2 py-1 border rounded-md" />
            <span>to</span>
            <input type="number" value={maxY} onChange={e => setMaxY(Number(e.target.value))} className="w-20 px-2 py-1 border rounded-md" />
        </div>
        {error && <p className="text-red-500">{error}</p>}
      </div>
    </div>
  );
};

export default LineChartComponent;
